import React from 'react';
import { render, hydrate } from 'react-dom';
import { hot } from 'react-hot-loader';
import ErrorBoundary from 'core/components/ErrorBoundary';
import ContactSeller from 'core/components/ContactSeller/ContactSeller';
import RatingStars from 'core/components/RatingStars';
import Pagination from 'core/components/Pagination/PaginationContainer';
import Analytics from 'core/lib/Analytics';
import { captureException, captureMessage } from 'error_tracker';
import { LoginStatic } from 'core/components/Login';
import globalListeners from 'base/listeners/globals';
import SidebarSignupWidget from 'core/components/SidebarSignupWidget/SidebarSignupWidget';
import loopQuery from 'core/utils/loopQuery';
import getAppData, { getCustomData } from 'core/utils/getAppData';
import Header from 'core/components/Header/HeaderContainer';
import Footer from 'core/components/Footer/Footer';
import Notification from 'core/components/Notification';
import LoginModalHoc from 'core/hoc/LoginModalHoc';
import { QuestionTypes } from 'core/utils';
import { UserRecs } from 'core/components/Recs';
import ResponsiveProvider from 'core/providers/ResponsiveProvider';

class CoreComponents {
	static base() {
		CoreComponents.header();
		CoreComponents.footer();
		CoreComponents.contactSeller();
		CoreComponents.pagination();
		CoreComponents.unhandledRejection();
		CoreComponents.staticLogin();
		CoreComponents.sidebarEmailSignup();
		CoreComponents.notifications();
		CoreComponents.loginWidget();
		CoreComponents.userRecs();
		Analytics.base();
		globalListeners();
	}

	static header() {
		const header = document.getElementById('react-header');
		if (! header) return null;

		const headerProps = getCustomData(window.cj.header_props, {});

		hydrate(<Header {...headerProps} />, header);
	}

	static footer() {
		const footer = document.getElementById('footer');
		if (! footer) return null;

		const footerProps = getCustomData(window.cj.footer_props, {});

		hydrate(<Footer {...footerProps} />, footer);
	}

	static staticLogin() {
		const target = document.getElementById('react-static-login');
		if (! target) return null;
		const loginView = target.getAttribute('data-login-view');
		render(<LoginStatic loginView={loginView} />, target);
	}

	static pagination() {
		const paginationEl = document.getElementById('pagination-react');
		if (! paginationEl) return null;

		let props = {
			...paginationEl.dataset,
		};

		render(<Pagination {...props} />, paginationEl);
	}

	static contactSeller() {
		const user = getCustomData(window.cj.user, {});
		const isUser = !!user.id;
		const contactBtns = document.querySelectorAll('.ContactSellerButton');
		if (! contactBtns) return;
		loopQuery(contactBtns, button => {
			const triggerLogin = window.cj.loginModal ? window.cj.loginModal.triggerLoginModal : () => console.error("Login modal not defined!");
			let props = {
				...button.dataset,
				fullWidth: button.dataset.fullWidth === 'true',
				triggerLogin,
			};
			if (props.listingId) {
				props.listingId = parseInt(props.listingId);
			}
			if (props.customerId) {
				props.customerId = parseInt(props.customerId);
			}
			if (props.purchase && props.purchase === 'pre_purchase') {
				props.questionType = QuestionTypes.pre_purchase;
			}
			render(
				<ErrorBoundary>
					<ContactSeller
						isLoggedIn={isUser}
						triggerLogin={triggerLogin}
						formProps={{ ...props }}
						{...props}
					/>
				</ErrorBoundary>,
				button
			);
		});
	}

	static ratingStars() {
		const starsElements = document.querySelectorAll('.RatingStars');
		if (! starsElements.length) return;
		loopQuery(starsElements, star => {
			let data = star.dataset;
			let props = {
				slug: data.slug,
				stars: parseFloat(data.stars) || 0,
				reviews: parseInt(data.reviews) || 0,
				tiny: !!data.tiny || false,
			};
			render(
				<ErrorBoundary>
					<RatingStars {...props} />
				</ErrorBoundary>,
				star
			);
		});
	}

	static sidebarEmailSignup() {
		const widgetElements = document.querySelectorAll('.sidebar-email-signup');
		if (! widgetElements.length) return;
		loopQuery(widgetElements, widgetElement => {
			const props = {
				minimal: widgetElement.classList.contains('minimal'),
			};
			render(
				<ErrorBoundary>
					<ResponsiveProvider>
						<SidebarSignupWidget {...props} />
					</ResponsiveProvider>
				</ErrorBoundary>,
				widgetElement
			);
		});
	}

	static unhandledRejection() {
		window.addEventListener('unhandledrejection', (err)  => {
			const reason = err.reason;
			if (reason instanceof Error) {
				captureException(reason);
			} else if (typeof reason === 'string') {
				captureMessage(reason);
			}
		});
	}

	static notifications() {
		const notifEl = document.getElementById('cj-notif');
		if (! notifEl) return;
		const message = notifEl.dataset.message;
		if (! message) return;
		render(<Notification message={message} delay={10000} />, notifEl);
	}

	static loginWidget() {
		// This needs to exist on the front-end listing page
		// to make available `window.cj.loginModal`
		// TODO: Remove once `wishlist_view`
		//				are ported to react
		const widgetEl = document.getElementById('login-modal-widget');
		if (!widgetEl) return;
		render(
			<ErrorBoundary>
				<LoginModalHoc contextUsage="backbone-widget-login">
					{({ triggerLoginModal }) => null}
				</LoginModalHoc>
			</ErrorBoundary>,
			widgetEl
		);
	}

	static userRecs() {
		const userRecsEl = document.getElementById('UserRecs');
		if (!userRecsEl) return;

		const user = getCustomData(window.cj.user, {});
		const isUser = !!user.id;
		const pageType = window?.cj?.page_type || 'base';
		const dataProps = userRecsEl.dataset || {};
		const extraProps = { ...dataProps };
		if (extraProps.noPadding === "true") {
			extraProps.noPadding = true; // cast to bool
		}
		const props = {
			show: isUser,
			isLoggedIn: isUser,
			favoriteIds: getAppData('fav_listing_ids', []),
			tracking: { pt: pageType },
			...extraProps,
		};
		render(
			<UserRecs {...props} />,
			userRecsEl
		);
	}
}

export default hot(module)(CoreComponents);
